import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue"
import _imports_0 from '@/assets/icons/arrow-right.svg'


const _hoisted_1 = { class: "flex py-5" }
const _hoisted_2 = { class: "content-center mr-2" }
const _hoisted_3 = {
  key: 0,
  class: "text-base-bold"
}
const _hoisted_4 = { class: "gap-x-1 flex mx-auto" }
const _hoisted_5 = ["data-test-id", "onClick"]
const _hoisted_6 = {
  key: 1,
  class: "text-base-bold text-racq-blue !py-2 flex items-center"
}
const _hoisted_7 = { class: "content-center ml-2" }
const _hoisted_8 = {
  key: 0,
  class: "text-base-bold"
}

import { getScreenSize } from '@/utils/dom-helpers';
import { computed, nextTick, ref, watch } from 'vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'Pagination',
  props: {
    total: {},
    itemsPerPage: {},
    defaultPage: {},
    nextBtnLabel: {},
    prevBtnLabel: {}
  },
  emits: ['prev', 'next', 'updatePage'],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const page = ref(props.defaultPage ?? 1);
watch(
  () => props.defaultPage,
  (newValue) => (page.value = newValue ?? 1)
);

const emit = __emit;

const numOfPages = computed(() => {
  return Math.ceil(props.total / props.itemsPerPage);
});
const maxVisiblePages = computed(() => {
  const sizes = getScreenSize();
  if (sizes.md.value) {
    return 5;
  } else if (sizes.sm.value || sizes.xse.value) {
    return 4;
  }
  return 7;
});
const showBtnLabel = computed(() => {
  return maxVisiblePages.value == 7;
});

const visiblePages = computed(() => {
  const totalPages = numOfPages.value;

  // If total pages fit within max visible pages, show all pages
  if (totalPages <= maxVisiblePages.value) {
    return new Array(totalPages).fill(0).map((_, index) => ({
      type: 'page',
      value: index + 1,
    }));
  }

  const currentPage = page.value;
  // Initialize items array with first page (always visible)
  const items: Array<{ type: 'page' | 'ellipsis'; value?: number }> = [{ type: 'page', value: 1 }];

  // Calculate how many pages to show before and after current page
  // Reserve space for first page (1) and last page, so subtract 2 from max visible
  let numbersBeforeCurrentPage,
    numbersAfterCurrentPage = (numbersBeforeCurrentPage = Math.floor((maxVisiblePages.value - 2) / 2));

  // Special case for even max visible pages
  if (numbersAfterCurrentPage + numbersBeforeCurrentPage + 1 > maxVisiblePages.value - 2) {
    numbersBeforeCurrentPage -= 1;
  }

  // Adjust numbers if current page is near the end - shift more pages to the left
  if (currentPage + numbersAfterCurrentPage >= totalPages) {
    numbersBeforeCurrentPage += currentPage + numbersAfterCurrentPage - totalPages + 1;
  }

  // Iterate through pages 2 to totalPages-1 (first and last are handled separately)
  let i = 2;
  while (i < totalPages) {
    const prevItemIsNotEllipsis = items[items.length - 1]?.type != 'ellipsis';

    // If page is too far before current page, show ellipsis
    if (i < currentPage - numbersBeforeCurrentPage) {
      prevItemIsNotEllipsis && items.push({ type: 'ellipsis' });
    }
    // If we haven't reached max visible pages yet, add the page number
    else if (items.filter((item) => item.type == 'page').length < maxVisiblePages.value - 1) {
      items.push({ type: 'page', value: i });
    }
    // If page is too far after current page, show ellipsis
    else if (i > currentPage + numbersAfterCurrentPage) {
      prevItemIsNotEllipsis && items.push({ type: 'ellipsis' });
    }
    i++;
  }

  // Always show last page
  items.push({ type: 'page', value: totalPages });
  return items;
});

const showPrev = computed(() => page.value > 1);
const showNext = computed(() => page.value < numOfPages.value);

const prev = () => {
  page.value -= 1;
  emit('prev');
  emit('updatePage', page.value);
};

const next = () => {
  page.value += 1;
  emit('next');
  emit('updatePage', page.value);
};

const updatePage = (p: number) => {
  page.value = p;
  emit('updatePage', p);
};

const focus = (p: number) => {
  nextTick(() => (document.querySelector(`[data-test-id=page_${p}]`) as HTMLButtonElement)?.focus());
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      (showPrev.value)
        ? (_openBlock(), _createElementBlock("button", {
            key: 0,
            class: "!min-w-0 btn btn-md btn-flat link flex",
            onClick: prev
          }, [
            _cache[0] || (_cache[0] = _createElementVNode("img", {
              class: "w-3 min-w-3 mr-1 scale-x-[-1]",
              src: _imports_0,
              alt: "prev"
            }, null, -1)),
            (showBtnLabel.value)
              ? (_openBlock(), _createElementBlock("span", _hoisted_3, _toDisplayString(_ctx.prevBtnLabel ?? 'Previous'), 1))
              : _createCommentVNode("", true)
          ]))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("ol", _hoisted_4, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visiblePages.value, (item, index) => {
        return (_openBlock(), _createElementBlock("li", { key: index }, [
          (item.type === 'page')
            ? (_openBlock(), _createElementBlock("button", {
                key: 0,
                "data-test-id": 'page_' + item.value,
                class: _normalizeClass([
            'text-base-bold link btn btn-md btn-flat !min-w-0 !py-2 !px-4',
            page.value == item.value && '!bg-racq-white',
          ]),
                onClick: ($event: any) => {
            updatePage(item.value!);
            focus(item.value!);
          }
              }, _toDisplayString(item.value), 11, _hoisted_5))
            : (_openBlock(), _createElementBlock("span", _hoisted_6, ".."))
        ]))
      }), 128))
    ]),
    _createElementVNode("div", _hoisted_7, [
      (showNext.value)
        ? (_openBlock(), _createElementBlock("button", {
            key: 0,
            class: "!min-w-0 btn btn-md btn-flat link flex ml-auto",
            onClick: next
          }, [
            (showBtnLabel.value)
              ? (_openBlock(), _createElementBlock("span", _hoisted_8, _toDisplayString(_ctx.nextBtnLabel ?? 'Next'), 1))
              : _createCommentVNode("", true),
            _cache[1] || (_cache[1] = _createElementVNode("img", {
              class: "w-3 min-w-3 ml-1",
              src: _imports_0,
              alt: "next"
            }, null, -1))
          ]))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})